/**
 * TripForm component - Form for creating/editing trips
 * Redesigned to match NewTripCreation.html
 */

import { useState, FormEvent } from 'react';
import { formatDateForInput, parseDate } from '../../utils/dates';
import type { Trip, CreateTripInput } from '../../types/trip';

interface TripFormProps {
  trip?: Trip;
  onSubmit: (data: CreateTripInput) => void;
  onCancel: () => void;
  desktopMode?: boolean;
}

export default function TripForm({ trip, onSubmit, onCancel, desktopMode = false }: TripFormProps) {
  const [name, setName] = useState(trip?.name || '');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState(
    trip ? formatDateForInput(trip.startDate) : ''
  );
  const [endDate, setEndDate] = useState(
    trip ? formatDateForInput(trip.endDate) : ''
  );
  const [travelers, setTravelers] = useState(2);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Date input states for placeholder behavior
  const [startDateType, setStartDateType] = useState<'text' | 'date'>('text');
  const [endDateType, setEndDateType] = useState<'text' | 'date'>('text');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});
    const newErrors: Record<string, string> = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = '請輸入旅程名稱';
    }

    // Validate dates
    if (!startDate) {
      newErrors.startDate = '請選擇開始日期';
    }
    if (!endDate) {
      newErrors.endDate = '請選擇結束日期';
    }

    if (startDate && endDate) {
      const start = parseDate(startDate);
      const end = parseDate(endDate);
      if (end < start) {
        newErrors.endDate = '結束日期不能早於開始日期';
      }
    }

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit form
    try {
      onSubmit({
        name: name.trim(),
        startDate: parseDate(startDate),
        endDate: parseDate(endDate),
        tags: [],
      });
    } catch (error) {
      if (error instanceof Error) {
        setErrors({ submit: error.message });
      }
    }
  };

  const incrementTravelers = () => {
    setTravelers(prev => prev + 1);
  };

  const decrementTravelers = () => {
    setTravelers(prev => Math.max(1, prev - 1));
  };

  return (
    <div className={desktopMode ? 'flex flex-col w-full' : 'relative flex h-full min-h-screen w-full flex-col overflow-x-hidden bg-background-light'}>
      {/* Header — mobile only */}
      {!desktopMode && (
        <header className="sticky top-0 z-10 flex items-center justify-between bg-background-light/90 px-4 py-3 backdrop-blur-md">
          <div className="w-8"></div>
          <h2 className="text-lg font-bold leading-tight tracking-tight text-slate-900">
            {trip ? '編輯旅程' : '新增旅程'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>
      )}

      {/* Main Content */}
      <main className={desktopMode ? 'flex-1 p-10' : 'flex-1 px-5 pb-32'}>
        {/* Hero / Headline — mobile only */}
        {!desktopMode && (
          <div className="pt-6 pb-6">
            <div className="mb-4 h-32 w-full overflow-hidden rounded-2xl relative shadow-sm">
              <img
                alt="山景與夕陽的旅行風景"
                className="h-full w-full object-cover opacity-90"
                src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=400&fit=crop"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-3 left-4 text-white">
                <p className="text-xs font-medium uppercase tracking-wider opacity-90">
                  {trip ? '編輯旅程' : '開始規劃'}
                </p>
              </div>
            </div>
            <h1 className="text-3xl font-extrabold leading-tight text-slate-900">
              {trip ? '更新您的' : '規劃新的'}
              <br />
              <span className="text-[#0dccf2]">冒險旅程</span>
            </h1>
          </div>
        )}

        {/* Desktop title section */}
        {desktopMode && (
          <div className="mb-7">
            <h2 className="text-2xl font-bold text-slate-900">建立新旅程</h2>
            <p className="mt-1 text-sm text-slate-400">填寫以下資訊，開始規劃你的旅程</p>
          </div>
        )}

        {/* Form Fields */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Trip Name Input */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-slate-900">旅程名稱</label>
            <input
              className="h-14 w-full rounded-xl border border-slate-200 bg-white px-4 text-base font-medium text-slate-900 placeholder:text-slate-400 focus:border-[#0dccf2] focus:outline-none focus:ring-1 focus:ring-[#0dccf2]"
              placeholder="例如：夏季度假 2024"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Destination Input */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-slate-900">去哪裡？</label>
            <input
              className="h-14 w-full rounded-xl border border-slate-200 bg-white px-4 text-base font-medium text-slate-900 placeholder:text-slate-400 focus:border-[#0dccf2] focus:outline-none focus:ring-1 focus:ring-[#0dccf2]"
              placeholder="城市、國家或機場"
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
          </div>

          {/* Date Selection */}
          <div className="flex gap-4">
            <div className="flex flex-1 flex-col gap-2">
              <label className="text-sm font-semibold text-slate-900">開始日期</label>
              <div className="relative">
                <input
                  className="h-14 w-full rounded-xl border border-slate-200 bg-white px-4 text-base font-medium text-slate-900 placeholder:text-slate-400 focus:border-[#0dccf2] focus:outline-none focus:ring-1 focus:ring-[#0dccf2]"
                  placeholder="選擇日期"
                  type={startDateType}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onFocus={() => setStartDateType('date')}
                  onBlur={() => !startDate && setStartDateType('text')}
                />
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              {errors.startDate && (
                <p className="text-sm text-red-600">{errors.startDate}</p>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <label className="text-sm font-semibold text-slate-900">結束日期</label>
              <div className="relative">
                <input
                  className="h-14 w-full rounded-xl border border-slate-200 bg-white px-4 text-base font-medium text-slate-900 placeholder:text-slate-400 focus:border-[#0dccf2] focus:outline-none focus:ring-1 focus:ring-[#0dccf2]"
                  placeholder="選擇日期"
                  type={endDateType}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onFocus={() => setEndDateType('date')}
                  onBlur={() => !endDate && setEndDateType('text')}
                />
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              {errors.endDate && (
                <p className="text-sm text-red-600">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Travelers Counter */}
          <div className="flex flex-col gap-2 pt-2">
            <label className="text-sm font-semibold text-slate-900">誰要一起去？</label>
            <div className="flex h-16 items-center justify-between rounded-xl border border-slate-200 bg-white px-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0dccf2]/10 text-[#0dccf2]">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <span className="text-base font-medium text-slate-900">旅行者</span>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={decrementTravelers}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="w-4 text-center text-lg font-bold text-slate-900">{travelers}</span>
                <button
                  type="button"
                  onClick={incrementTravelers}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0dccf2] text-white shadow-sm transition hover:bg-[#0dccf2]/90"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Error message */}
          {errors.submit && (
            <div className="text-red-600 text-sm">{errors.submit}</div>
          )}

          {/* Desktop submit button */}
          {desktopMode && (
            <button
              type="submit"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0dccf2] h-[52px] text-base font-bold text-white shadow-sm transition hover:bg-[#0dccf2]/90"
            >
              <span>{trip ? '更新旅程' : '創建旅程'}</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          )}
        </form>
      </main>

      {/* Fixed Bottom CTA — mobile only */}
      {!desktopMode && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-background-light/80 p-5 backdrop-blur-md">
          <button
            onClick={handleSubmit}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0dccf2] py-4 text-center text-lg font-bold text-white shadow-lg shadow-[#0dccf2]/30 transition transform active:scale-[0.98]"
          >
            <span>{trip ? '更新旅程' : '創建旅程'}</span>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
